import React from 'react';
import { Dialog } from '../../ui/components/Dialog';
import { FeatherX, FeatherCheck, FeatherAlertCircle } from '@subframe/core';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { WalletBadge } from './WalletBadge';
import { BetaPill } from './BetaPill';
import type { WalletBrand } from '../../lib/wallet';

export type ConnectStatusVariant = 'waiting' | 'success' | 'error';

interface WalletConnectModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  wallet: WalletBrand;
  /** QR image source (Xaman qr_png URL, or a generated data URL for WalletConnect). */
  qrUrl?: string;
  /** Deep / universal link to open the wallet app on mobile. */
  deepLink?: string;
  statusText?: string;
  statusVariant?: ConnectStatusVariant;
}

/**
 * WalletConnectModal — one themed connect modal shared by every wallet (multi-wallet).
 *
 * Dark, on-brand (semantic tokens + the app's IBM Plex Mono), with a framed QR, a
 * per-wallet branded "Open in {wallet}" deep link, and a live status line. Xaman and
 * Joey both flow through this so the connect experience is consistent.
 */
export const WalletConnectModal: React.FC<WalletConnectModalProps> = ({
  open,
  onClose,
  title,
  subtitle = 'Scan the QR code or tap the button below',
  wallet,
  qrUrl,
  deepLink,
  statusText,
  statusVariant = 'waiting',
}) => {
  const statusColor =
    statusVariant === 'success' ? 'text-success-600' : statusVariant === 'error' ? 'text-error-600' : 'text-subtext-color';

  return (
    <Dialog open={open} onOpenChange={(isOpen: boolean) => !isOpen && onClose()}>
      <Dialog.Content className="w-full max-w-[440px]">
        {/* Header */}
        <div className="flex w-full items-center justify-between gap-3 px-6 py-4">
          <div className="flex items-center gap-2 min-w-0">
            <WalletBadge wallet={wallet} size={32} />
            <span className="text-heading-3 font-heading-3 text-default-font truncate">{title}</span>
            {wallet.beta && <BetaPill />}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded hover:bg-neutral-100 transition-colors"
          >
            <FeatherX className="h-5 w-5 text-subtext-color" />
          </button>
        </div>
        <div className="w-full h-px bg-neutral-border" />

        {/* Body */}
        <div className="flex w-full flex-col items-center gap-5 px-6 py-6">
          <p className="text-body font-body text-subtext-color text-center">{subtitle}</p>

          {/* Beta heads-up — new wallet, may be rough; Xaman remains the stable option */}
          {wallet.beta && (
            <div className="w-full rounded-md border border-solid border-warning-200 bg-warning-50 px-3 py-2">
              <p className="text-caption font-caption text-warning-700 text-center">
                {wallet.name} support is new and still in beta. If you hit an issue, you can sign in with Xaman instead.
              </p>
            </div>
          )}

          {/* Framed QR — white quiet zone scans reliably on the dark surface */}
          {qrUrl && (
            <div className="rounded-xl bg-white p-3 shadow-sm">
              <img src={qrUrl} alt={`${wallet.name} QR code`} style={{ width: 220, height: 220, display: 'block' }} />
            </div>
          )}

          {/* Branded deep link */}
          {deepLink && (
            <a
              href={deepLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex w-full items-center justify-center rounded-md px-4 py-2.5 text-body-bold font-body-bold text-white transition-opacity hover:opacity-90"
              style={{ backgroundColor: wallet.accent }}
            >
              Open in {wallet.name}
            </a>
          )}

          {/* Live status */}
          {statusText && (
            <div className={`flex items-center gap-2 ${statusColor}`}>
              {statusVariant === 'waiting' && <LoadingSpinner size="sm" />}
              {statusVariant === 'success' && <FeatherCheck className="h-4 w-4" />}
              {statusVariant === 'error' && <FeatherAlertCircle className="h-4 w-4" />}
              <span className="text-body font-body">{statusText}</span>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
